import React, { useEffect, useRef } from 'react';
import styled from 'App/styled';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import { Variant } from 'shared/store/product/types';
import ShadeImage from './ShadeImage';

interface ShadesProps {
  variants: Variant[];
  currentVariantId: string;
  changeVariant: Function;
  isDisabledLazy: boolean;
}
interface PalletStyleProps {
  palletWidth: number;
}

const PalletWrapper = styled.div<PalletStyleProps>`
  ${({ theme }) => theme.borders.border100};
  border-radius: ${({ theme }) => theme.borders.radius10};
  border-color: ${({ theme }) => hexToRgb(theme.colors.state, 0.22)};
  overflow-x: ${({ palletWidth }) => (palletWidth > 400 ? 'scroll' : 'hidden')};
  overflow-y: hidden;
  width: 400px;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hexToRgb(theme.colors.state, 0.92)};
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => hexToRgb(theme.colors.state, 0.22)};
  }
`;
const Wrap = styled.div<PalletStyleProps>`
  padding: ${({ theme }) => theme.spacing.spacing60} 0
    ${({ theme }) => theme.spacing.spacing20}
    ${({ theme }) => theme.spacing.spacing40};
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  width: ${({ palletWidth }) => (palletWidth > 400 ? palletWidth : 400)}px;
`;

const Shade = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
  border: none;
  padding: ${({ theme }) => theme.spacing.spacing0};
  margin-right: ${({ theme }) => theme.spacing.spacing60};
  margin-bottom: ${({ theme }) => theme.spacing.spacing0};

  &:focus {
    outline: none;
  }
`;

const ShadePallet = ({
  variants,
  currentVariantId,
  changeVariant,
  isDisabledLazy,
}: ShadesProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentVariantIndex = variants.findIndex(
    (variant) => variant.childId === currentVariantId
  );
  let palletWidth = Math.ceil(variants.length / 2) * 46;

  useEffect(() => {
    const wrapper = wrapperRef?.current;
    const selectedShadeEle = document.getElementById(
      `variant_${currentVariantId}`
    ) as HTMLDivElement | null;
    if (wrapper && selectedShadeEle) {
      const left =
        selectedShadeEle.getBoundingClientRect().x -
        wrapper.getBoundingClientRect().left -
        10;
      wrapper?.scrollBy({
        left,
        behavior: 'smooth',
      });
    }
  }, [currentVariantId, currentVariantIndex]);

  return (
    <PalletWrapper
      palletWidth={palletWidth}
      ref={wrapperRef}
    >
      <Wrap palletWidth={palletWidth}>
        {variants.map((variant, index: number) => {
          const { shadeImage, variantName, quantity, childId } = variant;
          const isVariantSelected = childId === currentVariantId;
          const isVariantOutOfStock = quantity === 0;

          let additionalClassForShade = `${
            isVariantSelected && variant?.inStock ? 'active' : ''
          }`;

          if (isVariantOutOfStock) {
            additionalClassForShade += ' oos';
          }
          const additionalImageProps: JSX.IntrinsicElements['img'] = {};

          if (index > 4) {
            additionalImageProps.loading = 'lazy';
          }

          return (
            <Shade
              type="button"
              key={childId}
              onClick={() => changeVariant(childId)}
              id={`variant_${variant.childId}`}
              aria-label={`${variantName}${
                isVariantSelected ? ', selected' : ''
              }${isVariantOutOfStock ? ', unavailable' : ''}`}
            >
              <ShadeImage
                shadeImage={shadeImage || ''}
                variantName={variantName}
                additionalImageProps={additionalImageProps}
                additionalClassForShade={additionalClassForShade}
                isLazyLoad={!isDisabledLazy && currentVariantIndex < 4}
              />
            </Shade>
          );
        })}
      </Wrap>
    </PalletWrapper>
  );
};

export default ShadePallet;
